import { Filter, FilterType } from '@model/iceberg';
import { CheckboxFilter } from '@components/filters/v2/filters/checkbox';
import React from 'react';
import { RadioButtonFilter } from '@components/filters/v2/filters/radio/RadioButtonFilter';
import styled from '@emotion/styled';
import { useI18NextContext } from '@components/hooks';
import { StarRatingFilter } from './filters/stars';
import { ToggleFilter } from '@components/filters/v2/filters/toggle/ToggleFilter';
import { SortBy } from '@components/filters/v2/sort-by/SortBy';
import { mixins } from '@styles/mixins';
import { PriceFilter } from '@components/filters/v2/filters/price/PriceFilter';
import { TestId } from '@components/test-ids';
import { ThemeProps } from '@theme/base';

export interface FiltersSideBarProps {
  filters: Array<Filter>;
  hasAppliedFilters: boolean;
  onChange: (payload: Array<Filter>) => void;
  onReset: () => void;
  title?: boolean;
}

const Title = styled.h5(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h5 as any),
  padding: `2px ${theme.custom.spacing.medium}px`,
  margin: 0
}));

const Reset = styled.a(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  ...mixins(theme).link,
  fontWeight: 500,
  paddingLeft: theme.custom.spacing.xSmall
}));

enum FilterName {
  PROFILE = 'profile',
  MEALBASES = 'mealbases',
  TRIPADVISOR = 'mintripadvisorrating',
  PRICEBAND = 'priceband',
  RATINGS = 'ratings',
  FACILITIES = 'facilities'
}

export const FiltersSideBar = ({ filters, onChange, title, hasAppliedFilters, onReset }: FiltersSideBarProps) => {
  const t: any = useI18NextContext();
  const handleOnChange = (payload: Filter) =>
    onChange(filters.map((f: Filter) => (f.name === payload.name ? payload : f)));
  const renderFilters = () => {
    return filters.map((filter: Filter) => {
      switch (filter.type) {
        case FilterType.SORT:
          return <SortBy key={filter.name} testId={filter.name} filter={filter} onChange={handleOnChange} />;
        case FilterType.CHECKBOX:
          return (
            <CheckboxFilter
              key={filter.name}
              testId={filter.name}
              filter={filter}
              showCount={filter.name !== FilterName.MEALBASES}
              onChange={handleOnChange}
            />
          );
        case FilterType.STARS:
          return <StarRatingFilter key={filter.name} testId={filter.name} filter={filter} onChange={handleOnChange} />;
        case FilterType.RADIO:
          return <RadioButtonFilter key={filter.name} testId={filter.name} filter={filter} onChange={handleOnChange} />;
        case FilterType.TOGGLE:
          return <ToggleFilter key={filter.name} testId={filter.name} filter={filter} onChange={handleOnChange} />;
        case FilterType.PRICE:
          return <PriceFilter key={filter.name} testId={filter.name} filter={filter} onChange={handleOnChange} />;
        default:
          return <div key={filter.name}>{JSON.stringify(filter)}</div>;
      }
    });
  };
  if (!filters.length) return null;
  return (
    <div data-testid={TestId.dealFinder.filters.container}>
      {title && (
        <Title>
          {t('filters__toggle-button--label')}
          {hasAppliedFilters && (
            <Reset data-testid={TestId.dealFinder.filters.hasAppliedFilters} onClick={onReset}>
              ({t('common__label--reset').toLowerCase()})
            </Reset>
          )}
        </Title>
      )}
      {renderFilters()}
    </div>
  );
};
